import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * Corporate searchable single-select.
 * Props:
 *  - items:    [{ code, name }]
 *  - common:   [code, code, ...]  (pinned to top under "Common markets")
 *  - value:    current code (or "")
 *  - onChange: (code) => void
 *  - placeholder, allowClear, testid, disabled, className
 *  - emptyLabel: string shown when value is "" (e.g. "All countries")
 */
export default function SearchableSelect({
  items = [],
  common = [],
  value,
  onChange,
  placeholder = "Select…",
  emptyLabel,
  allowClear = false,
  testid,
  disabled,
  hideCode = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrap = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 20); }, [open]);

  const byCode = useMemo(() => Object.fromEntries(items.map((i) => [i.code, i])), [items]);
  const currentLabel = value ? (byCode[value]?.name || value) : (emptyLabel || placeholder);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) => i.name.toLowerCase().includes(s) || i.code.toLowerCase().includes(s));
  }, [items, q]);

  const commonItems = useMemo(() => {
    if (!common.length) return [];
    const s = q.trim().toLowerCase();
    return common
      .map((code) => byCode[code])
      .filter(Boolean)
      .filter((i) => !s || i.name.toLowerCase().includes(s) || i.code.toLowerCase().includes(s));
  }, [common, byCode, q]);

  const restItems = useMemo(() => {
    if (!common.length) return filtered;
    const commonSet = new Set(common);
    return filtered.filter((i) => !commonSet.has(i.code));
  }, [filtered, common]);

  const pick = (code) => { onChange(code); setOpen(false); setQ(""); };

  return (
    <div ref={wrap} className={`relative ${className}`} data-testid={testid}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        data-testid={testid ? `${testid}-button` : undefined}
        className={`w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm bg-white flex items-center justify-between gap-2 ${disabled ? 'opacity-60 cursor-not-allowed' : 'hover:border-[#0047FF]'}`}
      >
        <span className={`truncate ${!value ? 'text-[#8A94A6]' : 'text-[#0A0D14]'}`}>
          {value && byCode[value] ? (
            hideCode ? byCode[value].name : (
              <>
                <span className="font-mono text-[10px] uppercase text-[#8A94A6] mr-1.5">{byCode[value].code}</span>
                {byCode[value].name}
              </>
            )
          ) : currentLabel}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {allowClear && value && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); pick(""); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); pick(""); } }}
              className="p-0.5 rounded hover:bg-[#F8F9FA] cursor-pointer"
              data-testid={testid ? `${testid}-clear` : undefined}
            >
              <X className="h-3 w-3 text-[#8A94A6]" />
            </span>
          )}
          <ChevronDown className={`h-3.5 w-3.5 text-[#8A94A6] transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 min-w-[240px] bg-white border border-[#E2E8F0] rounded-md shadow-xl max-h-80 overflow-hidden flex flex-col" data-testid={testid ? `${testid}-menu` : undefined}>
          <div className="relative border-b border-[#E2E8F0]">
            <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A94A6]" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              data-testid={testid ? `${testid}-search` : undefined}
              className="w-full pl-9 pr-3 py-2 text-sm outline-none"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {allowClear && !q && emptyLabel && (
              <button
                type="button"
                onClick={() => pick("")}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[#F8F9FA] ${!value ? 'text-[#0047FF] font-medium' : 'text-[#525B6A]'}`}
                data-testid={testid ? `${testid}-opt-any` : undefined}
              >
                {emptyLabel}
              </button>
            )}
            {commonItems.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium bg-[#F8F9FA]">Common</div>
                {commonItems.map((i) => (
                  <button
                    key={`c-${i.code}`}
                    type="button"
                    onClick={() => pick(i.code)}
                    data-testid={testid ? `${testid}-opt-${i.code}` : undefined}
                    className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[#F8F9FA] flex items-center gap-2 ${value === i.code ? 'text-[#0047FF] font-medium bg-[#0047FF]/[0.06]' : 'text-[#0A0D14]'}`}
                  >
                    {!hideCode && <span className="font-mono text-[10px] uppercase text-[#8A94A6] w-8 shrink-0">{i.code}</span>}
                    <span className="truncate">{i.name}</span>
                  </button>
                ))}
                {restItems.length > 0 && <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium bg-[#F8F9FA]">All</div>}
              </>
            )}
            {restItems.map((i) => (
              <button
                key={i.code}
                type="button"
                onClick={() => pick(i.code)}
                data-testid={testid ? `${testid}-opt-${i.code}` : undefined}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-[#F8F9FA] flex items-center gap-2 ${value === i.code ? 'text-[#0047FF] font-medium bg-[#0047FF]/[0.06]' : 'text-[#0A0D14]'}`}
              >
                {!hideCode && <span className="font-mono text-[10px] uppercase text-[#8A94A6] w-8 shrink-0">{i.code}</span>}
                <span className="truncate">{i.name}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-xs text-[#8A94A6] text-center">No matches for &quot;{q}&quot;</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
